import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence, useSpring, useTransform, useReducedMotion } from "framer-motion";
import {
  Check, ArrowRight, ChevronDown, Sparkles, Bot, Building2, Loader2,
  Volume2, VolumeX, Minus, Plus, Heart,
  // section icons — ebuosystem (1-17)
  Scale, Globe, Layout, Palette, Package, CreditCard, Boxes, Megaphone, TrendingUp,
  Headphones, BookOpen, Users, Briefcase, BarChart3, Shield, Crown,
  // automation modules (1502-1516)
  Workflow, GitBranch, Zap, Table, NotebookPen, Database, Bell, Calculator,
  FileBarChart, LayoutDashboard, UserCheck,
  // security controls (1601-1609)
  KeyRound, Smartphone, Cloud, Lock, UserCog, ShieldCheck, ShieldAlert, Eye,
  // website types (301-308)
  Layers, ShoppingBag, CalendarCheck, PenTool, MousePointerClick, UserCircle, Newspaper, Store,
  // social media types (1-7)
  ThumbsUp, Camera, Music2, PlaySquare, AtSign, Link2, Ghost,
} from "lucide-react";

import type { RootState } from "../../redux/store";
import {
  ENTERPRISE,
  TIER_TYPES,
  TIER_TYPE_ORDER,
  EBUOS_TIER_ORDER,
  ALL_TIER_ORDER,
  EBUOS_FEATURES,
  CATEGORY_PRICING,
  AUTOMATION_MODULES,
  SECURITY_CONTROLS,
  WEBSITE_TYPES,
  SOCIAL_MEDIA_TYPES,
  CUSTOMER_AGENT_ENTERPRISE_SCALE,
  computeCategorySubscription,
  computeEbuosSubscription,
  computeDigitalCreatorSubscription,
  computeAutomationSubscription,
  computeCustomerAgentSubscription,
  computeSecuritySubscription,
  computeWebsiteSubscription,
  type SubscriptionTier,
  type TierType,
} from "../../redux/subscriptionSlice";
import { getAppSubcategories } from "../../CATEGORIES/AppCategories";
import logo from "../../assets/app/logo.png";

/* ─────────────────────────────────────────────────────────────
   SUBSCRIPTION — white paper, hard-cut 3D sticker cards. Colour
   is cosmetic only (each product gets a candy hue so its deck,
   chips and switcher row read as one family) — the page itself
   stays a bright, cool paper-white, same aesthetic as the very
   first pass, not the dark Ebuos-brand rework.

   WHICH PRODUCTS SHOW UP, AND IN WHAT ORDER, is one plain array
   below (see SWITCHER_ENTRIES). Comment a line out with // to
   hide that product from the switcher; remove the // to bring it
   back. Nothing else needs to change.

   Ebuos Platform is the one entry that isn't a single product —
   picking it opens a second, smaller switcher for the priced
   business sections (Legal Business, Domain, Brand, …) PLUS an
   "Ebuos Collection" option that bundles all of them at once.
   THE TIER IS THE FEATURE SET on every one of these: a merchant
   never picks individual subcategories, they pick basic/premium/
   titanium/elite and that tier decides how much of the section
   they get — every card lists the section's full feature set,
   with anything the tier hasn't unlocked yet shown greyed out
   and struck through. Digital Creator, Automation and AI Support
   are each their own product with their own metering, so picking
   them shows their deck directly — no extra step.
───────────────────────────────────────────────────────────── */

/* ═══ Backend — one door in ═══ */
const FUNCTIONS_BASE = "https://europe-west3-ebuosystem.cloudfunctions.net";
const SUBSCRIPTIONS_ENDPOINT = `${FUNCTIONS_BASE}/createSubscriptionCheckout`;

/* The Enterprise tile is noise next to a €69 Legal Business card — only
   surface it once a deck's priciest displayed tier is already in that
   territory (or the quote itself already routed to sales). Compared
   against monthEquivalent so it's cadence-independent. */
const ENTERPRISE_VISIBLE_AT_EUR = 20_000;

/* ═══ Tokens ═══
   Paper is a cool paper-white, not cream. Ink is a blue-black so
   outlines read as drawn rather than printed. Five candy hues, each
   with a "deep" twin that becomes the solid block under the card —
   purely decorative, swap them for anything without touching layout.
   BRAND_GRADIENT echoes the EBUOS wordmark (blue → pink → orange →
   yellow → green) and is used sparingly — the cadence pill, the H1
   underline swoosh, the top brand bar — so it reads as a brand touch,
   not a full reskin of the hue system below. */
const PAPER      = "#F3F4FA";
const PAPER_CARD = "#FFFFFF";
const INK        = "#171526";
const INK_SOFT   = "#5C5A72";
const INK_FAINT  = "#9A98AE";
const BRAND_GRADIENT = "linear-gradient(100deg, #3355FF 0%, #7A5AF8 18%, #E8317B 38%, #FF5A36 56%, #FFC400 76%, #3DBE5B 100%)";

const HUES = {
  grape:  { base: "#7A5AF8", deep: "#4B2ED6", soft: "#EFEBFF" },
  cherry: { base: "#F2415E", deep: "#B8123A", soft: "#FFECF0" },
  sky:    { base: "#2BA8F0", deep: "#0B6FB0", soft: "#E7F5FF" },
  mint:   { base: "#12C08A", deep: "#087A5A", soft: "#E4FBF3" },
  butter: { base: "#FFC44D", deep: "#C98A00", soft: "#FFF6E0" },
} as const;
type HueName = keyof typeof HUES;

const eur = (n: number) =>
  new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR", maximumFractionDigits: n % 1 ? 2 : 0 }).format(n);

const compact = (n: number) =>
  n >= 1_000_000 ? `${+(n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0)}M`
  : n >= 1_000 ? `${+(n / 1_000).toFixed(n % 1_000 ? 1 : 0)}k`
  : `${n}`;

type AnyQuote = { contactSales?: boolean; totalCharged: number | null; [k: string]: any };
const isPriced = (q: AnyQuote | null): q is AnyQuote & { recurringCharge: number } =>
  !!q && !q.contactSales && "recurringCharge" in q;

/* Chips carry an already-sized icon node — appCategories.tsx hands out
   14px icons per subcategory; the dedicated automation/security/website
   pickers build the same shape below so ChipRail never needs to know
   the difference. */
type Chip = { id: number; name: string; icon: React.ReactNode };
const iconChip = (Icon: React.ElementType) => <Icon size={15} strokeWidth={2.6} />;

/* A single line on a tier card. `included: false` renders greyed out
   and struck through — "you don't have this one yet". Every product
   builds these; only the ebuosystem section/collection decks ever set
   included:false, since they're the only ones with a real locked/
   unlocked feature list. */
type BulletItem = { text: string; included: boolean };
const b = (text: string, included = true): BulletItem => ({ text, included });

const MODULE_ICONS: Record<number, React.ElementType> = {
  1502: Workflow, 1503: GitBranch, 1504: Zap, 1505: Table, 1506: NotebookPen,
  1507: Database, 1508: Megaphone, 1510: TrendingUp, 1511: Bell, 1512: Calculator,
  1513: FileBarChart, 1514: Users, 1515: LayoutDashboard, 1516: UserCheck,
};
const CONTROL_ICONS: Record<number, React.ElementType> = {
  1601: KeyRound, 1602: Smartphone, 1603: Cloud, 1604: Database, 1605: Lock,
  1606: UserCog, 1607: ShieldCheck, 1608: ShieldAlert, 1609: Eye,
};
const WEBSITE_ICONS: Record<number, React.ElementType> = {
  301: Layers, 302: ShoppingBag, 303: CalendarCheck, 304: PenTool,
  305: MousePointerClick, 306: UserCircle, 307: Newspaper, 308: Store,
};
const SOCIAL_ICONS: Record<number, React.ElementType> = {
  1: ThumbsUp, 2: Camera, 3: Music2, 4: PlaySquare, 5: AtSign, 6: Link2, 7: Ghost,
};
const SECTION_ICONS: Record<number, React.ElementType> = {
  1: Scale, 2: Globe, 3: Layout, 4: Palette, 5: Package, 6: CreditCard, 7: Boxes,
  8: Megaphone, 9: TrendingUp, 10: Headphones, 11: BookOpen, 12: Users, 13: Briefcase,
  14: BarChart3, 15: Bot, 16: Shield, 17: Crown,
};
const HUE_ORDER: HueName[] = ["grape", "cherry", "sky", "mint", "butter"];
const hueForSection = (id: number): HueName => HUE_ORDER[(id - 1) % HUE_ORDER.length];

/* ═══════════════════════════════════════════════════════════
   WHICH PRODUCTS SHOW UP — comment a line to hide it, uncomment
   to bring it back. Order here is render order in the switcher.
═══════════════════════════════════════════════════════════ */
type ProductKind = "ebuosystem" | "digitalCreator" | "automation" | "customerAgent" | "security" | "website";
type SwitcherEntry = { id: ProductKind; name: string; hue: HueName; Icon: React.ElementType; blurb: string };

const EBUOSYSTEM_ENTRY: SwitcherEntry   = { id: "ebuosystem",   name: "Ebuos Platform",  hue: "grape",  Icon: Sparkles,   blurb: "Every business section, priced by tier — pick what you need." };
const DIGITAL_CREATOR_ENTRY: SwitcherEntry = { id: "digitalCreator", name: "Digital Creator", hue: "cherry", Icon: Bot,        blurb: "Writes, schedules and posts across your channels." };
const AUTOMATION_ENTRY: SwitcherEntry   = { id: "automation",   name: "Automation",      hue: "sky",    Icon: Workflow,   blurb: "Workflows and integrations that run themselves." };
const CUSTOMER_AGENT_ENTRY: SwitcherEntry = { id: "customerAgent", name: "AI Support",     hue: "mint",   Icon: Headphones, blurb: "Answers customers around the clock." };
const SECURITY_ENTRY: SwitcherEntry     = { id: "security",     name: "Security",        hue: "butter", Icon: Shield,     blurb: "Priced by what there is to defend." };
const WEBSITE_ENTRY: SwitcherEntry      = { id: "website",      name: "Website",         hue: "grape",  Icon: Layout,     blurb: "A real custom build, start to finish." };

const SWITCHER_ENTRIES: SwitcherEntry[] = [
  EBUOSYSTEM_ENTRY,
  DIGITAL_CREATOR_ENTRY,
   AUTOMATION_ENTRY,
  CUSTOMER_AGENT_ENTRY,
   SECURITY_ENTRY,
   WEBSITE_ENTRY,
];

/* ═══════════════════════════════════════════════════════════
   SOUND — six-oscillator toy kit, no audio files.
═══════════════════════════════════════════════════════════ */
function useSfx(on: boolean) {
  const ctxRef = useRef<AudioContext | null>(null);
  const onRef = useRef(on);
  onRef.current = on;

  const ctx = () => {
    if (typeof window === "undefined") return null;
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) return null;
    if (!ctxRef.current) ctxRef.current = new Ctor();
    if (ctxRef.current.state === "suspended") ctxRef.current.resume();
    return ctxRef.current;
  };

  const blip = useCallback(
    (from: number, to: number, dur: number, type: OscillatorType, vol: number, delay = 0) => {
      if (!onRef.current) return;
      const c = ctx();
      if (!c) return;
      const t = c.currentTime + delay;
      const osc = c.createOscillator();
      const gain = c.createGain();
      osc.type = type;
      osc.frequency.setValueAtTime(from, t);
      osc.frequency.exponentialRampToValueAtTime(Math.max(40, to), t + dur);
      gain.gain.setValueAtTime(0.0001, t);
      gain.gain.exponentialRampToValueAtTime(vol, t + 0.012);
      gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      osc.connect(gain).connect(c.destination);
      osc.start(t);
      osc.stop(t + dur + 0.02);
    },
    []
  );

  return useMemo(
    () => ({
      tick: () => blip(880, 1180, 0.05, "square", 0.035),
      untick: () => blip(560, 380, 0.06, "square", 0.03),
      pop: () => { blip(420, 880, 0.09, "triangle", 0.09); blip(880, 1320, 0.13, "sine", 0.05, 0.06); },
      chime: () => { blip(660, 660, 0.14, "sine", 0.07); blip(990, 990, 0.2, "sine", 0.05, 0.08); blip(1320, 1320, 0.26, "sine", 0.04, 0.16); },
      thud: () => blip(180, 90, 0.22, "sawtooth", 0.06),
      slide: () => blip(300, 460, 0.04, "sine", 0.02),
      whoosh: () => blip(240, 720, 0.16, "sine", 0.045),
    }),
    [blip]
  );
}
type SfxKit = ReturnType<typeof useSfx>;

/* ═══ Rolling price ═══ */
function Rolling({ value }: { value: number }) {
  const reduce = useReducedMotion();
  const spring = useSpring(value, { stiffness: 140, damping: 20, mass: 0.6 });
  useEffect(() => { spring.set(value); }, [value, spring]);
  const text = useTransform(spring, (v) => eur(Math.round(v * 100) / 100));
  if (reduce) return <>{eur(value)}</>;
  return <motion.span>{text}</motion.span>;
}

/* ═══ Burst — twelve pips, thrown once on select ═══ */
function Burst({ seed, hue }: { seed: number; hue: HueName }) {
  const reduce = useReducedMotion();
  const pips = useMemo(
    () => Array.from({ length: 12 }, (_, i) => ({
      a: (i / 12) * Math.PI * 2 + (seed % 7) * 0.11,
      d: 46 + ((i * 37 + seed) % 40),
      s: 5 + ((i * 13 + seed) % 5),
      r: (i % 2 ? 1 : -1) * (90 + (i * 23) % 180),
    })),
    [seed]
  );
  if (reduce || !seed) return null;
  return (
    <div className="eb-burst" aria-hidden>
      {pips.map((p, i) => (
        <motion.span
          key={`${seed}-${i}`}
          initial={{ x: 0, y: 0, scale: 0, rotate: 0, opacity: 1 }}
          animate={{ x: Math.cos(p.a) * p.d, y: Math.sin(p.a) * p.d, scale: 1, rotate: p.r, opacity: 0 }}
          transition={{ duration: 0.72, ease: [0.15, 0.9, 0.3, 1] }}
          style={{
            width: p.s, height: p.s * (i % 3 ? 1 : 1.8),
            background: i % 2 ? HUES[hue].base : HUES[hue].deep,
            borderRadius: i % 3 ? 2 : 999,
          }}
        />
      ))}
    </div>
  );
}

/* ═══ Chip rail ═══ */
function ChipRail({
  chips, selected, onToggle, hue, single = false,
}: { chips: Chip[]; selected: number[]; onToggle: (id: number) => void; hue: HueName; single?: boolean }) {
  if (!chips.length) return null;
  return (
    <div className="eb-rail" role={single ? "radiogroup" : "group"}>
      {chips.map((c) => {
        const on = selected.includes(c.id);
        return (
          <motion.button
            key={c.id} type="button" role={single ? "radio" : "switch"} aria-checked={on}
            className="eb-chip" data-on={on || undefined}
            style={{ "--h": HUES[hue].base, "--hd": HUES[hue].deep, "--hs": HUES[hue].soft } as React.CSSProperties}
            onClick={() => onToggle(c.id)} whileTap={{ y: 3 }} layout
          >
            {c.icon}
            <span>{c.name}</span>
            <AnimatePresence>
              {on && (
                <motion.span className="eb-chip-check"
                  initial={{ scale: 0, rotate: -40 }} animate={{ scale: 1, rotate: 0 }} exit={{ scale: 0 }}
                  transition={{ type: "spring", stiffness: 500, damping: 18 }}>
                  <Check size={11} strokeWidth={4} />
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
        );
      })}
    </div>
  );
}

/* ═══ Log-scaled dial ═══ */
function ScaleDial({
  label, unit, min, max, value, onChange, hue, onSlide,
}: { label: string; unit: string; min: number; max: number; value: number; onChange: (n: number) => void; hue: HueName; onSlide: () => void }) {
  const toT = (v: number) => Math.log(Math.max(min, v) / min) / Math.log(max / min);
  const fromT = (t: number) => {
    const raw = min * Math.pow(max / min, t);
    const step = raw < 100 ? 1 : raw < 1_000 ? 10 : raw < 100_000 ? 100 : raw < 1_000_000 ? 1_000 : 10_000;
    return Math.round(raw / step) * step;
  };
  return (
    <div className="eb-dial" style={{ "--h": HUES[hue].base, "--hd": HUES[hue].deep } as React.CSSProperties}>
      <div className="eb-dial-head">
        <span className="eb-label">{label}</span>
        <span className="eb-dial-value">{compact(value)} <em>{unit}</em></span>
      </div>
      <input type="range" min={0} max={1000} step={1} value={Math.round(toT(value) * 1000)} aria-label={label}
        onChange={(e) => { onChange(fromT(Number(e.target.value) / 1000)); onSlide(); }} />
      <div className="eb-dial-ends"><span>{compact(min)}</span><span>{compact(max)}</span></div>
    </div>
  );
}

function Stepper({
  label, value, min, max, onChange, onTick,
}: { label: string; value: number; min: number; max: number; onChange: (n: number) => void; onTick: () => void }) {
  const set = (n: number) => { onChange(Math.min(max, Math.max(min, n))); onTick(); };
  return (
    <div className="eb-stepper">
      <span className="eb-label">{label}</span>
      <div className="eb-stepper-ctl">
        <button type="button" onClick={() => set(value - 1)} aria-label={`One fewer ${label}`}><Minus size={14} strokeWidth={3.2} /></button>
        <strong>{value}</strong>
        <button type="button" onClick={() => set(value + 1)} aria-label={`One more ${label}`}><Plus size={14} strokeWidth={3.2} /></button>
      </div>
    </div>
  );
}

/* ═══ Tier card — the vertical sticker ═══
   Root is a div-with-role="button" (not a real <button>) so the "View
   all N" toggle below can be a genuine nested <button> — a real button
   can't legally live inside another real button. Card selection still
   works via click/Enter/Space on the div; the toggle stops propagation
   so expanding the list never fires onSelect. */
const FEATURE_LIST_COLLAPSE_AT = 20;
const FEATURE_LIST_PREVIEW_COUNT = 10;

function TierCard({
  name, price, cadenceShort, bullets, hue, selected, busy, disabled, ribbon, burstSeed, onSelect, ctaLabel,
}: {
  name: string; price: number | null; cadenceShort: string; bullets: BulletItem[]; hue: HueName;
  selected: boolean; busy: boolean; disabled: boolean; ribbon?: string; burstSeed: number;
  onSelect: () => void; ctaLabel: string;
}) {
  const reduce = useReducedMotion();
  const rx = useSpring(0, { stiffness: 200, damping: 18 });
  const ry = useSpring(0, { stiffness: 200, damping: 18 });
  const [expanded, setExpanded] = useState(false);

  const tilt = (e: React.MouseEvent<HTMLDivElement>) => {
    if (reduce) return;
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 12);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 12);
  };

  const canCollapse = bullets.length > FEATURE_LIST_COLLAPSE_AT;
  const visibleBullets = canCollapse && !expanded ? bullets.slice(0, FEATURE_LIST_PREVIEW_COUNT) : bullets;
  const activate = () => { if (!disabled) onSelect(); };

  return (
    <motion.div
      role="button" tabIndex={disabled ? -1 : 0} aria-pressed={selected} aria-disabled={disabled || undefined}
      layout className="eb-card" data-selected={selected || undefined} data-disabled={disabled || undefined}
      onMouseMove={tilt} onMouseLeave={() => { rx.set(0); ry.set(0); }}
      onClick={activate}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); activate(); } }}
      style={{ rotateX: rx, rotateY: ry, "--h": HUES[hue].base, "--hd": HUES[hue].deep, "--hs": HUES[hue].soft } as any}
      whileTap={reduce || disabled ? undefined : { y: 8 }}
      transition={{ type: "spring", stiffness: 320, damping: 26 }}
    >
      <Burst seed={burstSeed} hue={hue} />
      <AnimatePresence>
        {ribbon && (
          <motion.span className="eb-ribbon"
            initial={{ scale: 0, rotate: 12 }} animate={{ scale: 1, rotate: 8 }} exit={{ scale: 0 }}
            transition={{ type: "spring", stiffness: 460, damping: 16 }}>
            {ribbon}
          </motion.span>
        )}
      </AnimatePresence>
      <span className="eb-card-name">{name}</span>
      <span className="eb-card-price">
        {price === null ? <em className="eb-talk">Let's talk</em> : <><Rolling value={price} /><i>{cadenceShort}</i></>}
      </span>
      <span className="eb-card-rule" />
      <span className="eb-card-list">
        {visibleBullets.map((item, idx) => (
          <span key={idx} className="eb-bullet" data-off={!item.included || undefined}>
            {item.included ? <Check size={13} strokeWidth={3.4} /> : <Lock size={12} strokeWidth={2.6} />}
            <span className="eb-bullet-text">{item.text}</span>
          </span>
        ))}
      </span>
      {canCollapse && (
        <button type="button" className="eb-viewall" onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}>
          {expanded ? "Show less" : `View all ${bullets.length}`}
          <motion.span animate={{ rotate: expanded ? 180 : 0 }} transition={{ type: "spring", stiffness: 400, damping: 24 }}>
            <ChevronDown size={12} strokeWidth={3} />
          </motion.span>
        </button>
      )}
      <span className="eb-cta">
        {busy ? <Loader2 size={15} className="eb-spin" /> : selected ? <Check size={15} strokeWidth={3.4} /> : <ArrowRight size={15} strokeWidth={3} />}
        {busy ? "Saving" : ctaLabel}
      </span>
    </motion.div>
  );
}

function EnterpriseCard({ hue, onClick, blurb, wide }: { hue: HueName; onClick: () => void; blurb?: string; wide?: boolean }) {
  return (
    <div className="eb-card eb-card-ent" data-wide={wide || undefined}
      style={{ "--h": HUES[hue].base, "--hd": HUES[hue].deep, "--hs": HUES[hue].soft } as React.CSSProperties}>
      <span className="eb-card-name">{ENTERPRISE.name}</span>
      <span className="eb-card-price"><em className="eb-talk">Let's talk</em></span>
      <span className="eb-card-rule" />
      <span className="eb-card-list">
        <span className="eb-bullet eb-bullet-note">{blurb || ENTERPRISE.blurb}</span>
        {ENTERPRISE.includes.slice(0, 3).map((f) => <span key={f} className="eb-bullet"><Check size={13} strokeWidth={3.4} />{f}</span>)}
      </span>
      <button type="button" className="eb-cta eb-cta-ent" onClick={onClick}>
        <Building2 size={15} strokeWidth={2.8} />{ENTERPRISE.cta}
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   3D DROPDOWN SWITCHER — reused for both the top product picker
   and the nested Ebuos-section picker; items just need an id,
   name, hue and icon.
═══════════════════════════════════════════════════════════ */
function DropdownSwitcher({
  items, activeId, onSelect, sfx, eyebrow, size = "lg",
}: {
  items: { id: string; name: string; hue: HueName; Icon: React.ElementType }[];
  activeId: string; onSelect: (id: string) => void; sfx: SfxKit; eyebrow: string; size?: "lg" | "sm";
}) {
  const [open, setOpen] = useState(false);
  const active = items.find((s) => s.id === activeId) ?? items[0];
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  if (!active) return null;

  return (
    <div className={`eb-switcher${size === "sm" ? " eb-switcher-sm" : ""}`} ref={rootRef}>
      <button
        type="button" className="eb-switcher-current" aria-haspopup="listbox" aria-expanded={open}
        style={{ "--h": HUES[active.hue].base, "--hd": HUES[active.hue].deep, "--hs": HUES[active.hue].soft } as React.CSSProperties}
        onClick={() => { setOpen((o) => !o); sfx.slide(); }}
      >
        <span className="eb-switcher-icon"><active.Icon size={size === "sm" ? 15 : 19} strokeWidth={2.6} /></span>
        <span className="eb-switcher-text">
          <em>{eyebrow}</em>
          <strong>{active.name}</strong>
        </span>
        <motion.span className="eb-switcher-chevron" animate={{ rotate: open ? 180 : 0 }} transition={{ type: "spring", stiffness: 400, damping: 24 }}>
          <ChevronDown size={size === "sm" ? 14 : 17} strokeWidth={3} />
        </motion.span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="eb-switcher-panel" role="listbox" aria-label={eyebrow}
            initial={{ opacity: 0, rotateX: -22, y: -10, scale: 0.96 }}
            animate={{ opacity: 1, rotateX: 0, y: 0, scale: 1 }}
            exit={{ opacity: 0, rotateX: -16, y: -8, scale: 0.97 }}
            transition={{ type: "spring", stiffness: 400, damping: 28 }}
          >
            {items.map((s) => (
              <button
                key={s.id} type="button" role="option" aria-selected={s.id === activeId}
                className="eb-switcher-option" data-active={s.id === activeId || undefined}
                style={{ "--h": HUES[s.hue].base, "--hd": HUES[s.hue].deep, "--hs": HUES[s.hue].soft } as React.CSSProperties}
                onClick={() => { onSelect(s.id); setOpen(false); sfx.tick(); }}
              >
                <span className="eb-switcher-option-icon"><s.Icon size={15} strokeWidth={2.6} /></span>
                <span className="eb-switcher-option-name">{s.name}</span>
                {s.id === activeId && <Check size={14} strokeWidth={3.4} />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PAGE
═══════════════════════════════════════════════════════════ */
export default function Subscription() {
  const { merchantId: paramMerchantId } = useParams<{ merchantId: string }>();
  const currentMerchant = useSelector((s: RootState) => (s as any).merchant?.currentMerchant);
  const merchantId = paramMerchantId || currentMerchant?.merchantId || "";

  const [cadence, setCadence] = useState<TierType>("monthly");
  const [soundOn, setSoundOn] = useState(true);
  const sfx = useSfx(soundOn);

  const [toast, setToast] = useState<{ kind: "ok" | "error"; text: string } | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [burst, setBurst] = useState<{ key: string; seed: number }>({ key: "", seed: 0 });
  const [saved, setSaved] = useState<Record<string, string>>({});
  const [dirty, setDirty] = useState<Record<string, boolean>>({});

  const [activeProduct, setActiveProduct] = useState<ProductKind>(SWITCHER_ENTRIES[0]?.id ?? "ebuosystem");
  /* which Ebuos section, only relevant while activeProduct === "ebuosystem".
     "0" is reserved for the Ebuos Collection (every priced section bundled
     onto one tier) — it's not a real EBUOS_FEATURES id, just the first
     entry in the section switcher below. */
  const [activeSectionId, setActiveSectionId] = useState<number>(0);

  const [channelsByPlatform, setChannelsByPlatform] = useState<Record<number, number>>({ 1: 2 });
  const [modules, setModules] = useState<number[]>([1502, 1504]);
  const [tasks, setTasks] = useState(5_000);
  const [workflows, setWorkflows] = useState(10);
  const [conversations, setConversations] = useState(2_000);
  const [seats, setSeats] = useState(3);
  const [endpoints, setEndpoints] = useState(25);
  const [backupGb, setBackupGb] = useState(100);
  const [controls, setControls] = useState<number[]>([1601, 1602]);
  const [websiteType, setWebsiteType] = useState<number>(302);

  const cadenceShort = TIER_TYPES.find((t) => t.name === cadence)!.short;

  const showToast = useCallback((kind: "ok" | "error", text: string) => {
    setToast({ kind, text });
    window.setTimeout(() => setToast(null), 4000);
  }, []);

  const markDirty = (product: string) => setDirty((d) => ({ ...d, [product]: true }));

  const toggler = (product: string, setter: React.Dispatch<React.SetStateAction<number[]>>) => (id: number) => {
    setter((prev) => {
      const on = prev.includes(id);
      on ? sfx.untick() : sfx.tick();
      return on ? prev.filter((x) => x !== id) : [...prev, id];
    });
    markDirty(product);
  };

  const selectProduct = (id: string) => { setActiveProduct(id as ProductKind); sfx.whoosh(); };
  const selectSection = (id: string) => { setActiveSectionId(Number(id)); sfx.whoosh(); };

  /* Section switcher: "Ebuos Collection" first, then every section that
     actually has a flat-tier ladder (CATEGORY_PRICING). Website, AI &
     Automation and Security are deliberately absent here — they're full
     products with their own tabs up top, not part of this ladder. */
  const sectionDefs = useMemo(() => [
    { id: "0", name: "Ebuos Collection", hue: "grape" as HueName, Icon: Sparkles },
    ...EBUOS_FEATURES
      .filter((f) => !!CATEGORY_PRICING[f.id])
      .map((f) => ({ id: String(f.id), name: f.name, hue: hueForSection(f.id), Icon: SECTION_ICONS[f.id] || Sparkles })),
  ], []);

  /* Real subcategory names for whichever section is active — used to
     LABEL every feature on that section's cards (checked if the tier
     unlocks it, struck through if not). There is no picker: the tier
     alone decides the count, this just names the first N. */
  const sectionFeatures = useMemo(() => getAppSubcategories(activeSectionId), [activeSectionId]);

  const genericQuotes = useMemo(
    () => EBUOS_TIER_ORDER.map((t) => computeCategorySubscription({ categoryId: activeSectionId, tier: t, cadence })),
    [activeSectionId, cadence]
  );
  const collectionQuotes = useMemo(
    () => EBUOS_TIER_ORDER.map((t) => computeEbuosSubscription({ tier: t, cadence })),
    [cadence]
  );
  const creator = useMemo(() => ALL_TIER_ORDER.map((t) => computeDigitalCreatorSubscription({ tier: t, channelsByPlatform, cadence })), [channelsByPlatform, cadence]);
  const autom = useMemo(() => ALL_TIER_ORDER.map((t) => computeAutomationSubscription({ tier: t, tasksPerMonth: tasks, activeWorkflows: workflows, moduleIds: modules, cadence })), [tasks, workflows, modules, cadence]);
  const agent = useMemo(() => ALL_TIER_ORDER.map((t) => computeCustomerAgentSubscription({ tier: t, conversationsPerMonth: conversations, seats, cadence })), [conversations, seats, cadence]);
  const secure = useMemo(() => ALL_TIER_ORDER.map((t) => computeSecuritySubscription({ tier: t, endpoints, backupGb, controlIds: controls, cadence })), [endpoints, backupGb, controls, cadence]);
  const site = useMemo(() => computeWebsiteSubscription({ typeId: websiteType, cadence }), [websiteType, cadence]);

  const save = useCallback(
    async (opts: { product: string; key: string; categoryId: number | null; subcategoryIds: number[]; tier?: string; scale?: Record<string, number>; channelsByPlatform?: Record<number, number>; savedAs: string }) => {
      if (!merchantId) {
        sfx.thud();
        showToast("error", "No merchant selected. Open this page from a merchant's dashboard.");
        return;
      }
      setBusyKey(opts.key);
      sfx.pop();
      setBurst({ key: opts.key, seed: Date.now() % 100000 });
      try {
        const res = await fetch(SUBSCRIPTIONS_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            merchantId,
            product: opts.product,
            categoryId: opts.categoryId,
            subcategoryIds: opts.subcategoryIds,
            tier: opts.tier ?? null,
            cadence,
            scale: opts.scale ?? {},
            channelsByPlatform: opts.channelsByPlatform ?? null,
          }),
        });

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          // 409 = this configuration needs a sales conversation, not a hard error
          if (res.status === 409) {
            throw new Error(data.error || "This plan needs a quick chat with sales first.");
          }
          throw new Error(data.error || `Request failed (${res.status})`);
        }

        if (!data.url) {
          throw new Error("Checkout session was created but no redirect URL was returned.");
        }

        sfx.chime();
        window.location.href = data.url; // hand off to Stripe Checkout
        return; // stop here — the browser is navigating away
      } catch (err: any) {
        sfx.thud();
        showToast("error", err?.message || "Couldn't reach the server. Try again.");
      } finally {
        setBusyKey(null);
      }
    },
    [merchantId, cadence, sfx, showToast]
  );

  const contactSales = useCallback(() => { sfx.pop(); window.open(ENTERPRISE.contactUrl, "_blank", "noopener,noreferrer"); }, [sfx]);

  const moduleChips: Chip[] = useMemo(() => AUTOMATION_MODULES.map((m) => ({ id: m.id, name: m.name, icon: iconChip(MODULE_ICONS[m.id] || Workflow) })), []);
  const socialChips: Chip[] = useMemo(() => SOCIAL_MEDIA_TYPES.map((s) => ({ id: s.id, name: s.label, icon: iconChip(SOCIAL_ICONS[s.id] || Bot) })), []);
  const controlChips: Chip[] = useMemo(() => SECURITY_CONTROLS.map((c) => ({ id: c.id, name: c.name, icon: iconChip(CONTROL_ICONS[c.id] || Shield) })), []);
  const websiteChips: Chip[] = useMemo(() => WEBSITE_TYPES.map((w) => ({ id: w.id, name: w.name, icon: iconChip(WEBSITE_ICONS[w.id] || Layers) })), []);

  const cardCta = (product: string, tier: string) => (saved[product] === tier ? (dirty[product] ? "Update plan" : "Active") : "Choose this");

  /* subcategoryIds can be a fixed array (every other product) or a
     function of the quote (the ebuosystem category deck) — the tier
     itself determines how many of that section's features are included,
     so the ids to persist on save depend on which quote/tier was picked. */
  const renderDeck = (
    product: string, hue: HueName, quotes: AnyQuote[],
    bulletsFor: (q: AnyQuote) => BulletItem[],
    meta: { categoryId: number | null; subcategoryIds: number[] | ((q: AnyQuote) => number[]); scale: Record<string, number>; channelsByPlatform?: Record<number, number> },
    popular: SubscriptionTier,
    isEligible?: (q: AnyQuote) => boolean,
  ) => {
    /* Scale (channels/tasks/conversations/endpoints) can force several
       REQUESTED tiers onto the same EFFECTIVE tier — e.g. 200 channels
       lifts starter through titanium all onto Elite. Show that tier's
       card once, not once per requested slot that landed on it. */
    const seen = new Set<string>();
    const cards = quotes.filter((q) => {
      const t = String(q.tier);
      if (seen.has(t)) return false;
      seen.add(t);
      return isEligible ? isEligible(q) : true;
    });

    const highestPriced = [...cards].reverse().find((q) => isPriced(q)) as (AnyQuote & { monthEquivalent: number }) | undefined;
    const showEnterprise = cards.some((q) => q.contactSales) || (!!highestPriced && highestPriced.monthEquivalent >= ENTERPRISE_VISIBLE_AT_EUR);

    return (
      <div className="eb-grid">
        {cards.map((q) => {
          const tier = q.tier as SubscriptionTier;
          const key = `${product}:${tier}`;
          const priced = isPriced(q);
          const ids = typeof meta.subcategoryIds === "function" ? meta.subcategoryIds(q) : meta.subcategoryIds;
          return (
            <TierCard
              key={key} name={q.tierName} price={priced ? q.recurringCharge : null} cadenceShort={cadenceShort}
              bullets={bulletsFor(q)} hue={hue} selected={saved[product] === tier} busy={busyKey === key} disabled={!!busyKey}
              ribbon={saved[product] === tier ? "Yours" : tier === popular ? "Most picked" : undefined}
              burstSeed={burst.key === key ? burst.seed : 0} ctaLabel={cardCta(product, tier)}
              onSelect={() => save({ product, key, categoryId: meta.categoryId, subcategoryIds: ids, tier, scale: meta.scale, channelsByPlatform: meta.channelsByPlatform, savedAs: tier })}
            />
          );
        })}
        {showEnterprise && <EnterpriseCard hue={hue} onClick={contactSales} />}
      </div>
    );
  };

  const productEntry = SWITCHER_ENTRIES.find((e) => e.id === activeProduct) ?? SWITCHER_ENTRIES[0];
  if (!productEntry) return null; // every line in SWITCHER_ENTRIES is commented out — nothing to show

  const activeSection = sectionDefs.find((s) => s.id === String(activeSectionId)) ?? sectionDefs[0];

  return (
    <div className="eb">
      <style>{CSS}</style>
      <div className="eb-brandbar" aria-hidden />
      <div className="eb-paper" aria-hidden>
        <img src={logo} alt="" className="eb-watermark" />
        <span className="eb-blob b1" /><span className="eb-blob b2" /><span className="eb-blob b3" />
      </div>

      <div className="eb-shell">
        <header className="eb-top">
          <img src={logo} alt="Ebuos" style={{ height: 26, width: "auto" }} />
          <button type="button" className="eb-mute"
            onClick={() => { setSoundOn((s) => !s); if (!soundOn) window.setTimeout(() => sfx.tick(), 0); }} aria-pressed={soundOn}>
            {soundOn ? <Volume2 size={15} strokeWidth={2.8} /> : <VolumeX size={15} strokeWidth={2.8} />}
            {soundOn ? "Sound on" : "Sound off"}
          </button>
        </header>

        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 140, damping: 20 }}>
          <p className="eb-eyebrow">
            <Heart size={11} strokeWidth={0} fill="currentColor" className="eb-eyebrow-heart" aria-hidden />
            Subscription
          </p>
          <h1 className="eb-h1">
            Build the business,<br />
            <span className="eb-h1-mark">one card at a time.</span>
          </h1>
          <p className="eb-lede">Pick a product below, then pick a plan. Prices move live as you go.</p>
        </motion.div>

        <div className="eb-toprow">
          <DropdownSwitcher
            items={SWITCHER_ENTRIES.map((e) => ({ id: e.id, name: e.name, hue: e.hue, Icon: e.Icon }))}
            activeId={activeProduct} onSelect={selectProduct} sfx={sfx} eyebrow="Now building"
          />

          <div className="eb-cadence" role="radiogroup" aria-label="Billing cadence">
            {TIER_TYPE_ORDER.map((c) => {
              const on = cadence === c;
              return (
                <button key={c} type="button" role="radio" aria-checked={on} data-on={on || undefined}
                  onClick={() => { setCadence(c); sfx.slide(); }}>
                  {on && <motion.span layoutId="cadence-pill" className="eb-cadence-pill" transition={{ type: "spring", stiffness: 380, damping: 30 }} />}
                  <span>{TIER_TYPES.find((t) => t.name === c)!.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.section
            key={activeProduct}
            className="eb-stage"
            style={{ "--h": HUES[productEntry.hue].base, "--hd": HUES[productEntry.hue].deep, "--hs": HUES[productEntry.hue].soft } as React.CSSProperties}
            initial={{ opacity: 0, y: 16, scale: 0.99 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.99 }}
            transition={{ type: "spring", stiffness: 260, damping: 26 }}
          >
            <p className="eb-stage-blurb">{productEntry.blurb}</p>

            {activeProduct === "ebuosystem" && activeSection && (
              <>
                <DropdownSwitcher
                  items={sectionDefs} activeId={String(activeSectionId)} onSelect={selectSection} sfx={sfx}
                  eyebrow="Section" size="sm"
                />

                {activeSectionId === 0 ? (
                  renderDeck("ebuosCollection", "grape", collectionQuotes as AnyQuote[],
                    (q) => [
                      b(`All ${q.activeFeatures.length} sections included`),
                      b(`${Math.round(q.bundleDiscountPercent * 100)}% off the ${eur(q.listMonthlyTotal)} list price`),
                      ...q.perCategory.map((p: any) => b(p.categoryName, p.includedSubcategories >= p.totalSubcategories)),
                    ],
                    { categoryId: null, subcategoryIds: [], scale: {} }, "titanium")
                ) : (
                  renderDeck(`category:${activeSectionId}`, activeSection.hue, genericQuotes as AnyQuote[],
                    (q) => sectionFeatures.map((f, idx) => b(f.name, idx < q.includedSubcategories)),
                    { categoryId: activeSectionId, subcategoryIds: (q) => sectionFeatures.slice(0, q.includedSubcategories).map((c) => c.id), scale: {} },
                    "premium")
                )}
              </>
            )}

            {activeProduct === "digitalCreator" && (
              <>
                <div className="eb-platform-steppers">
                  {SOCIAL_MEDIA_TYPES.map((s) => (
                    <Stepper
                      key={s.id}
                      label={s.label}
                      value={channelsByPlatform[s.id] ?? 0}
                      min={0}
                      max={100}
                      onChange={(v) => {
                        setChannelsByPlatform((prev) => {
                          const next = { ...prev };
                          if (v <= 0) delete next[s.id]; else next[s.id] = v;
                          return next;
                        });
                        markDirty("digitalCreator");
                      }}
                      onTick={sfx.tick}
                    />
                  ))}
                </div>
                {(() => {
                  const requestedPlatformCount = Object.keys(channelsByPlatform).length;
                  const creatorQuotes = creator as AnyQuote[];
                  const eligible = creatorQuotes.some((q) => q.includedPlatformTypes >= requestedPlatformCount);
                  return eligible ? (
                    renderDeck("digitalCreator", "cherry", creatorQuotes,
                      (q) => [
                        b(`${q.postsIncluded?.toLocaleString()} posts ${cadenceShort}`),
                        b(`${q.connectedChannels} connected channels total`),
                        ...SOCIAL_MEDIA_TYPES.map((s, idx) => b(s.label, idx < q.includedPlatformTypes)),
                      ],
                      { categoryId: 15, subcategoryIds: [1501], scale: {}, channelsByPlatform }, "premium",
                      (q) => q.includedPlatformTypes >= requestedPlatformCount)
                  ) : (
                    <div className="eb-grid">
                      <EnterpriseCard hue="cherry" onClick={contactSales}
                        blurb={`You've picked ${requestedPlatformCount} platforms — even Elite only covers ${creatorQuotes[creatorQuotes.length - 1]?.includedPlatformTypes}. Let's talk about a custom plan.`} />
                    </div>
                  );
                })()}
              </>
            )}

            {activeProduct === "automation" && (
              <>
                <ChipRail chips={moduleChips} selected={modules} onToggle={toggler("automation", setModules)} hue="sky" />
                <div className="eb-controls">
                  <ScaleDial label="Tasks a month" unit="tasks" min={100} max={10_000_000} value={tasks}
                    onChange={(v) => { setTasks(v); markDirty("automation"); }} hue="sky" onSlide={sfx.slide} />
                  <ScaleDial label="Live workflows" unit="workflows" min={1} max={1_000} value={workflows}
                    onChange={(v) => { setWorkflows(v); markDirty("automation"); }} hue="sky" onSlide={sfx.slide} />
                </div>
                {renderDeck("automation", "sky", autom as AnyQuote[],
                  (q) => [
                    b(`${q.includedTasks?.toLocaleString()} tasks included`),
                    b(q.workflowAllowance === -1 ? "Unlimited live workflows" : `${q.workflowAllowance} live workflows`),
                    b(q.blockedModules?.length ? `${q.blockedModules.length} picks need a lift` : q.minIntervalMinutes === 0 ? "Instant triggers" : `Runs every ${q.minIntervalMinutes} min`),
                  ],
                  { categoryId: 15, subcategoryIds: modules, scale: { tasksPerMonth: tasks, activeWorkflows: workflows } }, "premium")}
              </>
            )}

            {activeProduct === "customerAgent" && (
              <>
                <div className="eb-controls">
                  <ScaleDial label="Conversations a month" unit="chats" min={10} max={1_500_000} value={conversations}
                    onChange={(v) => { setConversations(v); markDirty("customerAgent"); }} hue="mint" onSlide={sfx.slide} />
                  <Stepper label="Human seats" value={seats} min={1} max={200}
                    onChange={(v) => { setSeats(v); markDirty("customerAgent"); }} onTick={sfx.tick} />
                </div>
                {conversations > CUSTOMER_AGENT_ENTERPRISE_SCALE ? (
                  <div className="eb-grid">
                    <EnterpriseCard hue="mint" onClick={contactSales} wide
                      blurb="Past a million conversations a month we price it together — negotiated rates, dedicated capacity, signed SLA." />
                  </div>
                ) : (
                  renderDeck("customerAgent", "mint", agent as AnyQuote[],
                    (q) => [
                      b(`${q.includedConversations?.toLocaleString()} chats included`),
                      b(q.channels === -1 ? "Every channel" : `${q.channels} channels · ${q.languages === -1 ? "any" : q.languages} languages`),
                      b(q.effectiveCostPerConversation ? `${eur(q.effectiveCostPerConversation)} per chat, all in` : "Voice ready on Premium+"),
                    ],
                    { categoryId: 10, subcategoryIds: [1009, 1509], scale: { conversationsPerMonth: conversations, seats } }, "premium")
                )}
              </>
            )}

            {activeProduct === "security" && (
              <>
                <ChipRail chips={controlChips} selected={controls} onToggle={toggler("security", setControls)} hue="butter" />
                <div className="eb-controls">
                  <ScaleDial label="Protected endpoints" unit="endpoints" min={1} max={50_000} value={endpoints}
                    onChange={(v) => { setEndpoints(v); markDirty("security"); }} hue="butter" onSlide={sfx.slide} />
                  <ScaleDial label="Backup storage" unit="GB" min={1} max={100_000} value={backupGb}
                    onChange={(v) => { setBackupGb(v); markDirty("security"); }} hue="butter" onSlide={sfx.slide} />
                </div>
                {renderDeck("security", "butter", secure as AnyQuote[],
                  (q) => [
                    b(`${q.includedEndpoints} endpoints included`),
                    b(`${q.includedBackupGb?.toLocaleString()} GB of backups`),
                    b(q.monitoring === "none" ? `${q.logRetentionDays} days of logs` : `Monitoring: ${q.monitoring}`),
                  ],
                  { categoryId: 16, subcategoryIds: controls, scale: { endpoints, backupGb } }, "titanium")}
              </>
            )}

            {activeProduct === "website" && site && (
              <>
                <ChipRail chips={websiteChips} selected={[websiteType]}
                  onToggle={(id) => { setWebsiteType(id); sfx.tick(); markDirty("website"); }} hue="grape" single />
                <div className="eb-grid">
                  <TierCard
                    name={site.typeName} price={site.setupFee} cadenceShort=" build"
                    bullets={[
                      b(`${eur(site.hostingCharge)} hosting ${cadenceShort}`),
                      b(`${site.complexity} complexity build`),
                      b(`${eur(site.totalCharged ?? 0)} to start today`),
                    ]}
                    hue="grape" selected={saved.website === String(site.typeId)} busy={busyKey === `website:${site.typeId}`} disabled={!!busyKey}
                    ribbon={saved.website === String(site.typeId) ? "Yours" : undefined}
                    burstSeed={burst.key === `website:${site.typeId}` ? burst.seed : 0}
                    ctaLabel={cardCta("website", String(site.typeId))}
                    onSelect={() => save({ product: "website", key: `website:${site.typeId}`, categoryId: 3, subcategoryIds: [site.typeId], scale: {}, savedAs: String(site.typeId) })}
                  />
                  <EnterpriseCard hue="grape" onClick={contactSales} blurb="Something bigger, or several sites at once? We'll scope it properly." />
                </div>
              </>
            )}
          </motion.section>
        </AnimatePresence>

        <p className="eb-foot">Every change here is saved against your merchant account. Cancel or switch any time — nothing locks in.</p>
      </div>

      <AnimatePresence>
        {toast && (
          <motion.div className="eb-toast" data-error={toast.kind === "error" || undefined}
            initial={{ opacity: 0, y: 26, scale: 0.94 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 26, scale: 0.94 }}
            transition={{ type: "spring", stiffness: 420, damping: 26 }} role="status">
            {toast.kind === "ok" && <Check size={15} strokeWidth={3.4} />}
            {toast.text}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   STYLE — hard-cut sticker system on white paper. No blurred
   shadows anywhere: every raised thing sits on a solid block of
   its own colour with an ink outline, so the page reads as cut
   paper. Colour is cosmetic — swap HUES above without touching
   any of this.
═══════════════════════════════════════════════════════════ */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Baloo+2:wght@600;700;800&family=Outfit:wght@400;500;600&family=DM+Mono:wght@500&display=swap');

.eb, .eb *, .eb *::before, .eb *::after{ box-sizing:border-box; }
.eb{ position:relative; min-height:100vh; overflow-x:hidden; background:${PAPER}; color:${INK};
  font-family:'Outfit',system-ui,sans-serif; font-weight:450; -webkit-font-smoothing:antialiased;
  --brand: ${BRAND_GRADIENT}; }
.eb h1,.eb h2{ font-family:'Baloo 2',system-ui,cursive; font-weight:800; margin:0; line-height:1.02; letter-spacing:-.02em; }
.eb p{ margin:0; }
.eb button{ font-family:inherit; color:inherit; border:none; background:none; cursor:pointer; }
.eb :focus-visible{ outline:3px solid ${INK}; outline-offset:3px; }

.eb-brandbar{ position:fixed; top:0; left:0; right:0; height:4px; background:var(--brand); z-index:50; }

.eb-paper{ position:fixed; inset:0; z-index:0; pointer-events:none; overflow:hidden; }
.eb-watermark{ position:absolute; top:-8%; right:-10%; width:56vw; max-width:820px; opacity:.05;
  transform:rotate(-9deg); user-select:none; }
.eb-blob{ position:absolute; border-radius:48% 52% 41% 59%/55% 44% 56% 45%; opacity:.22; }
.eb-blob.b1{ width:44vw; height:44vw; left:-14vw; top:-12vw; background:${HUES.grape.base}; animation:ebDrift 34s ease-in-out infinite; }
.eb-blob.b2{ width:34vw; height:34vw; right:-10vw; top:22vh; background:${HUES.butter.base}; animation:ebDrift 41s ease-in-out infinite reverse; }
.eb-blob.b3{ width:40vw; height:40vw; left:18vw; bottom:-16vw; background:${HUES.mint.base}; animation:ebDrift 47s ease-in-out infinite; }
@keyframes ebDrift{ 0%,100%{ transform:translate3d(0,0,0) rotate(0deg); } 50%{ transform:translate3d(3vw,-4vh,0) rotate(24deg); } }

.eb-shell{ position:relative; z-index:1; max-width:1180px; margin:0 auto; padding:0 20px 96px; }

.eb-top{ display:flex; align-items:center; justify-content:space-between; padding:22px 0 30px; }
.eb-mute{ display:inline-flex; align-items:center; gap:7px; padding:9px 14px; border-radius:999px;
  background:${PAPER_CARD}; border:2.5px solid ${INK}; box-shadow:0 4px 0 ${INK};
  font-size:12px; font-weight:600; transition:transform .12s, box-shadow .12s; }
.eb-mute:active{ transform:translateY(4px); box-shadow:0 0 0 ${INK}; }

.eb-eyebrow{ display:inline-flex; align-items:center; gap:6px; font-family:'DM Mono',monospace; font-size:11px; letter-spacing:.22em; text-transform:uppercase; color:${INK_FAINT}; }
.eb-eyebrow-heart{ color:${HUES.cherry.base}; display:inline-flex; }
.eb-h1{ font-size:clamp(34px,5.6vw,60px); margin-top:12px; }
.eb-h1-mark{ position:relative; display:inline-block; }
.eb-h1-mark::after{ content:''; position:absolute; left:-2%; right:-2%; bottom:.08em; height:.34em; z-index:-1;
  background:var(--brand); border-radius:999px 999px 6px 999px; transform:rotate(-1.2deg); }
.eb-lede{ margin-top:14px; max-width:520px; font-size:15.5px; line-height:1.6; color:${INK_SOFT}; }

.eb-toprow{ display:flex; flex-wrap:wrap; align-items:center; gap:14px; margin-top:28px; }

/* switcher — used for both the top product picker and the nested section picker */
.eb-switcher{ position:relative; }
.eb-switcher-current{ display:flex; align-items:center; gap:12px; padding:12px 18px; border-radius:20px;
  background:var(--hs); border:2.5px solid var(--h); box-shadow:0 6px 0 var(--hd); min-width:260px;
  transition:transform .12s; }
.eb-switcher-current:active{ transform:translateY(3px); }
.eb-switcher-icon{ width:34px; height:34px; border-radius:11px; display:inline-flex; align-items:center; justify-content:center;
  background:var(--h); color:#fff; flex-shrink:0; }
.eb-switcher-text{ display:flex; flex-direction:column; align-items:flex-start; text-align:left; flex:1; }
.eb-switcher-text em{ font-style:normal; font-family:'DM Mono',monospace; font-size:9.5px; letter-spacing:.14em; text-transform:uppercase; color:${INK_FAINT}; }
.eb-switcher-text strong{ font-family:'Baloo 2',cursive; font-size:15.5px; margin-top:2px; color:${INK}; }
.eb-switcher-chevron{ color:${INK_SOFT}; flex-shrink:0; }
.eb-switcher-sm .eb-switcher-current{ min-width:0; padding:8px 14px; box-shadow:0 4px 0 var(--hd); }
.eb-switcher-sm .eb-switcher-icon{ width:26px; height:26px; border-radius:8px; }
.eb-switcher-sm .eb-switcher-text strong{ font-size:13.5px; }
.eb-switcher-sm{ margin-bottom:18px; }

.eb-switcher-panel{ position:absolute; top:calc(100% + 10px); left:0; z-index:40; width:min(360px, 90vw);
  max-height:420px; overflow-y:auto; padding:8px; border-radius:20px;
  background:${PAPER_CARD}; border:2.5px solid ${INK};
  box-shadow:0 10px 0 ${INK}; transform-style:preserve-3d; transform-origin:top center; }
.eb-switcher-option{ display:flex; align-items:center; gap:10px; width:100%; padding:10px 12px; border-radius:13px;
  font-size:13.5px; font-weight:550; color:${INK_SOFT}; text-align:left; }
.eb-switcher-option:hover{ background:${PAPER}; color:${INK}; }
.eb-switcher-option[data-active]{ background:var(--hs); color:${INK}; }
.eb-switcher-option-icon{ width:26px; height:26px; border-radius:8px; display:inline-flex; align-items:center; justify-content:center;
  background:var(--h); color:#fff; flex-shrink:0; }
.eb-switcher-option-name{ flex:1; }
.eb-switcher-option svg:last-child{ color:var(--h); }

/* cadence pill */
.eb-cadence{ display:inline-flex; gap:4px; padding:5px; border-radius:999px;
  background:${PAPER_CARD}; border:2.5px solid ${INK}; box-shadow:0 5px 0 ${INK}; }
.eb-cadence button{ position:relative; padding:9px 18px; border-radius:999px; font-size:13px; font-weight:600; color:${INK_SOFT}; }
.eb-cadence button[data-on]{ color:#fff; }
.eb-cadence button span{ position:relative; z-index:1; }
.eb-cadence-pill{ position:absolute; inset:0; border-radius:999px; background:var(--brand); z-index:0; }

.eb-stage{ margin-top:36px; }
.eb-stage-blurb{ font-size:13.5px; color:${INK_SOFT}; margin-bottom:18px; }

/* chips */
.eb-rail{ display:flex; flex-wrap:wrap; gap:9px; margin-bottom:20px; }
.eb-chip{ position:relative; display:inline-flex; align-items:center; gap:7px; padding:9px 14px; border-radius:999px;
  background:${PAPER_CARD}; border:2.5px solid ${INK}; box-shadow:0 4px 0 ${INK};
  font-size:12.5px; font-weight:550; transition:background .14s, box-shadow .12s, transform .12s; }
.eb-chip svg{ color:${INK_SOFT}; }
.eb-chip:hover{ transform:translateY(-2px); box-shadow:0 6px 0 ${INK}; }
.eb-chip[data-on]{ background:var(--h); color:#fff; box-shadow:0 4px 0 var(--hd); padding-right:30px; }
.eb-chip[data-on] svg{ color:#fff; }
.eb-chip-check{ position:absolute; right:8px; display:inline-flex; align-items:center; justify-content:center;
  width:15px; height:15px; border-radius:50%; background:#fff; color:var(--hd); }

/* dials */
.eb-platform-steppers{ display:grid; grid-template-columns:repeat(auto-fit,minmax(160px,1fr)); gap:12px; margin-bottom:22px; }
.eb-controls{ display:flex; flex-wrap:wrap; gap:14px; margin-bottom:22px; }
.eb-dial{ flex:1 1 260px; background:${PAPER_CARD}; border:2.5px solid ${INK}; border-radius:22px; padding:14px 16px 10px; box-shadow:0 5px 0 ${INK}; }
.eb-dial-head{ display:flex; align-items:baseline; justify-content:space-between; gap:10px; }
.eb-label{ font-family:'DM Mono',monospace; font-size:10px; letter-spacing:.14em; text-transform:uppercase; color:${INK_FAINT}; }
.eb-dial-value{ font-family:'Baloo 2',cursive; font-weight:700; font-size:19px; color:var(--hd); }
.eb-dial-value em{ font-family:'Outfit',sans-serif; font-style:normal; font-size:11px; font-weight:600; color:${INK_FAINT}; }
.eb-dial input[type=range]{ -webkit-appearance:none; appearance:none; width:100%; height:12px; margin:12px 0 4px;
  background:var(--hs); border:2.5px solid ${INK}; border-radius:999px; }
.eb-dial input[type=range]::-webkit-slider-thumb{ -webkit-appearance:none; width:24px; height:24px; border-radius:50%;
  background:var(--h); border:2.5px solid ${INK}; box-shadow:0 3px 0 var(--hd); cursor:grab; margin-top:-1px; }
.eb-dial input[type=range]::-moz-range-thumb{ width:22px; height:22px; border-radius:50%;
  background:var(--h); border:2.5px solid ${INK}; box-shadow:0 3px 0 var(--hd); cursor:grab; }
.eb-dial-ends{ display:flex; justify-content:space-between; font-family:'DM Mono',monospace; font-size:10px; color:${INK_FAINT}; }

.eb-stepper{ flex:0 0 auto; background:${PAPER_CARD}; border:2.5px solid ${INK}; border-radius:22px; padding:14px 16px; box-shadow:0 5px 0 ${INK}; }
.eb-stepper-ctl{ display:flex; align-items:center; gap:12px; margin-top:8px; }
.eb-stepper-ctl strong{ font-family:'Baloo 2',cursive; font-size:22px; min-width:34px; text-align:center; }
.eb-stepper-ctl button{ width:30px; height:30px; border-radius:10px; display:inline-flex; align-items:center; justify-content:center;
  background:${PAPER}; border:2.5px solid ${INK}; box-shadow:0 3px 0 ${INK}; transition:transform .1s, box-shadow .1s; }
.eb-stepper-ctl button:active{ transform:translateY(3px); box-shadow:0 0 0 ${INK}; }

/* the deck */
.eb-grid{ display:grid; grid-template-columns:repeat(auto-fit,minmax(216px,1fr)); gap:18px; perspective:1000px; align-items:start; }

.eb-card{ position:relative; display:flex; flex-direction:column; gap:11px; text-align:left; cursor:pointer;
  min-height:376px; padding:22px 20px; border-radius:28px;
  background:${PAPER_CARD}; border:2.5px solid ${INK}; box-shadow:0 9px 0 ${INK};
  transform-style:preserve-3d; transition:box-shadow .14s, background .18s; }
.eb-card:hover:not([data-disabled]):not(.eb-card-ent){ box-shadow:0 13px 0 var(--hd); background:var(--hs); }
.eb-card[data-disabled]{ opacity:.55; cursor:progress; pointer-events:none; }
.eb-card[data-selected]{ background:var(--hs); box-shadow:0 9px 0 var(--hd); }
.eb-card[data-wide]{ grid-column:1/-1; }

.eb-ribbon{ position:absolute; top:-13px; right:14px; padding:5px 11px; border-radius:999px;
  background:${INK}; color:#fff; font-family:'DM Mono',monospace; font-size:9.5px; letter-spacing:.12em; text-transform:uppercase; }
.eb-card[data-selected] .eb-ribbon{ background:var(--hd); }

.eb-card-name{ font-family:'Baloo 2',cursive; font-weight:700; font-size:21px; }
.eb-card-price{ font-family:'Baloo 2',cursive; font-weight:800; font-size:clamp(30px,3.4vw,38px); line-height:1; letter-spacing:-.02em; color:var(--hd); }
.eb-card-price i{ font-family:'Outfit',sans-serif; font-style:normal; font-size:12.5px; font-weight:600; color:${INK_FAINT}; margin-left:5px; }
.eb-talk{ font-style:normal; font-size:26px; }
.eb-card-rule{ height:2.5px; background:${INK}; border-radius:2px; opacity:.12; }
.eb-card-list{ display:flex; flex-direction:column; gap:9px; }
.eb-bullet{ display:flex; align-items:flex-start; gap:8px; font-size:12.5px; line-height:1.45; color:${INK_SOFT}; }
.eb-bullet svg{ flex-shrink:0; margin-top:2px; color:var(--hd); }
.eb-bullet[data-off]{ color:${INK_FAINT}; }
.eb-bullet[data-off] svg{ color:${INK_FAINT}; }
.eb-bullet[data-off] .eb-bullet-text{ text-decoration:line-through; text-decoration-color:${INK_FAINT}; }
.eb-bullet-note{ color:${INK_SOFT}; }

.eb-viewall{ align-self:flex-start; display:inline-flex; align-items:center; gap:5px; padding:6px 12px; margin-top:-2px;
  border-radius:999px; border:2px dashed ${INK_FAINT}; color:${INK_SOFT}; font-size:11px; font-weight:600;
  background:transparent; transition:border-color .12s, color .12s; }
.eb-viewall:hover{ border-color:var(--hd); color:var(--hd); }

.eb-cta{ margin-top:auto; display:inline-flex; align-items:center; justify-content:center; gap:8px;
  padding:13px 16px; border-radius:999px; font-family:'Baloo 2',cursive; font-weight:700; font-size:14px;
  background:${PAPER}; border:2.5px solid ${INK}; box-shadow:0 4px 0 ${INK}; transition:background .16s, box-shadow .12s; }
.eb-card:hover .eb-cta{ background:var(--h); color:#fff; box-shadow:0 4px 0 var(--hd); }
.eb-card[data-selected] .eb-cta{ background:var(--h); color:#fff; box-shadow:0 4px 0 var(--hd); }
.eb-cta-ent:active{ transform:translateY(4px); box-shadow:0 0 0 ${INK}; }

.eb-card-ent{ background:repeating-linear-gradient(135deg, ${PAPER_CARD} 0 12px, ${PAPER} 12px 24px); }

.eb-burst{ position:absolute; left:50%; top:44%; width:0; height:0; pointer-events:none; }
.eb-burst span{ position:absolute; display:block; }

.eb-foot{ margin-top:52px; font-size:12.5px; color:${INK_FAINT}; text-align:center; }

.eb-toast{ position:fixed; left:50%; bottom:26px; transform:translateX(-50%); z-index:60;
  display:flex; align-items:center; gap:9px; padding:14px 20px; border-radius:18px;
  background:${PAPER_CARD}; border:2.5px solid ${INK}; box-shadow:0 6px 0 ${INK};
  font-size:13px; font-weight:600; max-width:min(440px,calc(100vw - 40px)); }
.eb-toast[data-error]{ background:${HUES.cherry.soft}; box-shadow:0 6px 0 ${HUES.cherry.deep}; }

@keyframes ebSpin{ to{ transform:rotate(360deg); } }
.eb-spin{ animation:ebSpin .8s linear infinite; }

@media (max-width:560px){
  .eb-grid{ grid-template-columns:1fr; }
  .eb-card{ min-height:0; }
  .eb-toprow{ flex-direction:column; align-items:stretch; }
  .eb-switcher-current{ width:100%; }
  .eb-cadence{ width:100%; justify-content:space-between; }
  .eb-cadence button{ flex:1; padding:9px 6px; }
  .eb-watermark{ width:85vw; top:-3%; right:-24%; }
}

@media (prefers-reduced-motion:reduce){
  .eb-blob{ animation:none; }
  .eb *{ transition-duration:.01ms !important; }
}
`;